import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import { GooglePlacesAutocomplete } from '../components/ui/google-places-autocomplete';
import {
  Building2,
  User,
  Mail,
  Phone,
  Shield,
  ChevronRight,
  ChevronLeft,
  Loader2,
  CheckCircle,
  Eye,
  EyeOff,
  Users,
  FileText,
  Truck,
  HardHat,
  Shovel,
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: 'company', label: 'Company' },
  { id: 'account', label: 'First User' },
  { id: 'plan', label: 'Plan' },
  { id: 'terms', label: 'Terms' },
  { id: 'review', label: 'Review' },
];

const INCLUDED_OPERATORS = 2;
const INCLUDED_USERS = 1;

const COUNTRY_CODES = [
  { code: '+61', label: 'AU +61' },
  { code: '+64', label: 'NZ +64' },
  { code: '+1',  label: 'US +1' },
  { code: '+44', label: 'UK +44' },
  { code: '+353', label: 'IE +353' },
  { code: '+65', label: 'SG +65' },
  { code: '+91', label: 'IN +91' },
  { code: '+49', label: 'DE +49' },
  { code: '+971', label: 'AE +971' },
  { code: '+27', label: 'ZA +27' },
];

function PhoneInputWithCode({ value, onChange, countryCode, onCodeChange, placeholder, testId }) {
  return (
    <div className="flex gap-1.5">
      <Select value={countryCode} onValueChange={onCodeChange}>
        <SelectTrigger className="w-[100px] shrink-0" data-testid={`${testId}-code`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {COUNTRY_CODES.map(c => (
            <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input value={value} onChange={onChange} placeholder={placeholder} data-testid={testId} />
    </div>
  );
}

const INDUSTRY_ICONS = {
  crane: HardHat,
  earthmoving: Shovel,
  haulage: Truck,
};

export default function EarlyAdopterOnboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [industries, setIndustries] = useState([]);
  const [termsAvailable, setTermsAvailable] = useState(false);
  const [contactCountryCode, setContactCountryCode] = useState('+61');
  const [adminCountryCode, setAdminCountryCode] = useState('+61');

  const [form, setForm] = useState({
    company_name: '',
    address: '',
    abn: '',
    industry_type: '',
    admin_full_name: '',
    admin_email: '',
    admin_phone: '',
    admin_password: '',
    confirm_password: '',
    total_users: 1,
    total_workers_with_app_access: 2,
    terms_accepted: false,
    contact_email: '',
    contact_phone: '',
  });

  useEffect(() => {
    axios.get(`${API_URL}/api/ea/industries`).then(r => setIndustries(r.data.industries)).catch(() => {});
    axios.get(`${API_URL}/api/ea/terms-status`).then(r => setTermsAvailable(r.data.available)).catch(() => {});
  }, []);

  const set = useCallback((field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setError('');
  }, []);

  // ── Validation per step ──────────────────────────────────────────

  const validateStep = () => {
    switch (step) {
      case 0: // Company
        if (!form.company_name.trim()) return 'Company name is required';
        if (!form.address.trim()) return 'Address is required';
        if (!form.contact_email.trim()) return 'Company email is required';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contact_email)) return 'Invalid company email format';
        if (!form.contact_phone.trim()) return 'Company phone is required';
        if (!form.industry_type) return 'Industry is required';
        return null;
      case 1: // Account
        if (!form.admin_full_name.trim()) return 'Full name is required';
        if (!form.admin_email.trim()) return 'Email is required';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.admin_email)) return 'Invalid email format';
        if (form.admin_password.length < 8) return 'Password must be at least 8 characters';
        if (form.admin_password !== form.confirm_password) return 'Passwords do not match';
        return null;
      case 2: // Plan
        if (form.total_users < 1) return 'You need at least 1 user';
        if (form.total_workers_with_app_access < 1) return 'You need at least 1 worker';
        return null;
      case 3: // Terms
        if (!form.terms_accepted) return 'You must accept the Terms & Conditions';
        return null;
      default:
        return null;
    }
  };

  const handleNext = () => {
    const err = validateStep();
    if (err) { setError(err); return; }
    setError('');
    setStep(s => Math.min(s + 1, STEPS.length - 1));
  };

  const handleBack = () => { setError(''); setStep(s => Math.max(s - 1, 0)); };

  const handleSubmit = async () => {
    setError('');
    setLoading(true);
    try {
      const payload = {
        company_name: form.company_name.trim(),
        address: form.address.trim(),
        abn: form.abn.trim() || undefined,
        industry_type: form.industry_type,
        admin_full_name: form.admin_full_name.trim(),
        admin_email: form.admin_email.trim().toLowerCase(),
        admin_phone: form.admin_phone.trim() ? `${adminCountryCode} ${form.admin_phone.trim()}` : undefined,
        admin_password: form.admin_password,
        total_users: form.total_users,
        total_workers_with_app_access: form.total_workers_with_app_access,
        terms_accepted: form.terms_accepted,
        contact_email: form.contact_email.trim() || undefined,
        contact_phone: form.contact_phone.trim() ? `${contactCountryCode} ${form.contact_phone.trim()}` : undefined,
      };
      const res = await axios.post(`${API_URL}/api/ea/onboard`, payload);
      setSuccess(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Success Screen ───────────────────────────────────────────────

  if (success) {
    return (
      <div className="min-h-screen bg-[#121212] flex items-center justify-center p-4">
        <Card className="w-full max-w-lg border-[#ffd108]/30 bg-[#1c1c1c]">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-16 h-16 rounded-full bg-[#ffd108]/20 flex items-center justify-center mb-4">
              <CheckCircle className="h-8 w-8 text-[#ffd108]" />
            </div>
            <CardTitle className="text-2xl font-heading text-white">You're In!</CardTitle>
            <CardDescription className="text-zinc-400">
              {success.message}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-[#262626] rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">Company</span>
                <span className="text-white font-medium">{success.company_name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">Client ID</span>
                <span className="font-mono text-[#ffd108] font-bold">{success.client_id}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">Login Email</span>
                <span className="text-white">{success.admin_email}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">Tier</span>
                <Badge className="bg-[#ffd108] text-black text-xs">Advanced</Badge>
              </div>
            </div>
            <p className="text-xs text-zinc-500 text-center">A welcome email has been sent with your login details.</p>
            <Button className="w-full btn-volt" onClick={() => navigate('/login')} data-testid="ea-go-to-login">
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Step Renderers ───────────────────────────────────────────────

  const renderCompanyStep = () => (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="label-overline">Company Name <span className="text-red-500">*</span></Label>
        <div className="relative">
          <Building2 className="absolute left-3 top-2.5 h-4 w-4 text-zinc-500" />
          <Input className="pl-10" value={form.company_name} onChange={e => set('company_name', e.target.value)} placeholder="Your company name" data-testid="ea-company-name" />
        </div>
      </div>
      <div className="space-y-2">
        <Label className="label-overline">Address <span className="text-red-500">*</span></Label>
        <GooglePlacesAutocomplete
          value={form.address}
          onChange={e => set('address', typeof e === 'string' ? e : e?.target?.value ?? '')}
          onPlaceSelect={p => set('address', p?.formatted_address || form.address)}
          placeholder="Start typing your address..."
          data-testid="ea-address"
        />
      </div>
      <div className="space-y-2">
        <Label className="label-overline">ABN / Business Number</Label>
        <Input value={form.abn} onChange={e => set('abn', e.target.value)} placeholder="Optional" data-testid="ea-abn" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label className="label-overline">Company Email <span className="text-red-500">*</span></Label>
          <div className="relative">
            <Mail className="absolute left-3 top-2.5 h-4 w-4 text-zinc-500" />
            <Input className="pl-10" type="email" value={form.contact_email} onChange={e => set('contact_email', e.target.value)} placeholder="info@company.com" data-testid="ea-contact-email" />
          </div>
        </div>
        <div className="space-y-2">
          <Label className="label-overline">Company Phone <span className="text-red-500">*</span></Label>
          <PhoneInputWithCode
            value={form.contact_phone}
            onChange={e => set('contact_phone', e.target.value)}
            countryCode={contactCountryCode}
            onCodeChange={setContactCountryCode}
            placeholder="3000 0000"
            testId="ea-contact-phone"
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label className="label-overline">Industry <span className="text-red-500">*</span></Label>
        <Select value={form.industry_type} onValueChange={v => set('industry_type', v)}>
          <SelectTrigger data-testid="ea-industry"><SelectValue placeholder="Select your industry" /></SelectTrigger>
          <SelectContent>
            {industries.map(i => {
              const Icon = INDUSTRY_ICONS[i.value] || Truck;
              return (
                <SelectItem key={i.value} value={i.value}>
                  <span className="flex items-center gap-2"><Icon className="h-4 w-4" />{i.label}</span>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>
    </div>
  );

  const renderAccountStep = () => (
    <div className="space-y-4">
      <p className="text-sm text-zinc-400">This will be the first admin user on your account. You can add more users and workers after setup.</p>
      <div className="space-y-2">
        <Label className="label-overline">Full Name <span className="text-red-500">*</span></Label>
        <div className="relative">
          <User className="absolute left-3 top-2.5 h-4 w-4 text-zinc-500" />
          <Input className="pl-10" value={form.admin_full_name} onChange={e => set('admin_full_name', e.target.value)} placeholder="Your full name" data-testid="ea-admin-name" />
        </div>
      </div>
      <div className="space-y-2">
        <Label className="label-overline">Email <span className="text-red-500">*</span></Label>
        <div className="relative">
          <Mail className="absolute left-3 top-2.5 h-4 w-4 text-zinc-500" />
          <Input className="pl-10" type="email" value={form.admin_email} onChange={e => set('admin_email', e.target.value)} placeholder="you@company.com" data-testid="ea-admin-email" />
        </div>
      </div>
      <div className="space-y-2">
        <Label className="label-overline">Phone</Label>
        <PhoneInputWithCode
          value={form.admin_phone}
          onChange={e => set('admin_phone', e.target.value)}
          countryCode={adminCountryCode}
          onCodeChange={setAdminCountryCode}
          placeholder="0400 000 000"
          testId="ea-admin-phone"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label className="label-overline">Password <span className="text-red-500">*</span></Label>
          <div className="relative">
            <Input type={showPassword ? 'text' : 'password'} value={form.admin_password} onChange={e => set('admin_password', e.target.value)} placeholder="Min 8 characters" data-testid="ea-admin-password" />
            <button type="button" className="absolute right-2 top-2 text-zinc-500 hover:text-zinc-300" onClick={() => setShowPassword(!showPassword)}>
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
        <div className="space-y-2">
          <Label className="label-overline">Confirm Password <span className="text-red-500">*</span></Label>
          <Input type={showPassword ? 'text' : 'password'} value={form.confirm_password} onChange={e => set('confirm_password', e.target.value)} placeholder="Re-enter password" data-testid="ea-confirm-password" />
        </div>
      </div>
    </div>
  );

  const renderPlanStep = () => {
    const extraUsers = Math.max(0, form.total_users - INCLUDED_USERS);
    const extraWorkers = Math.max(0, form.total_workers_with_app_access - INCLUDED_OPERATORS);

    return (
      <div className="space-y-5">
        <div className="bg-[#262626] border border-[#ffd108]/30 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Badge className="bg-[#ffd108] text-black font-bold text-sm">Advanced</Badge>
            <span className="text-zinc-400 text-xs">Advanced Plan</span>
          </div>
          <p className="text-sm text-zinc-300">Full access to scheduling, dockets, reports, forms, compliance, timesheets, quotes, and more.</p>
          <div className="flex flex-wrap gap-2 mt-3">
            {['Scheduling', 'Dockets', 'Reports', 'Forms', 'Compliance', 'Timesheets', 'Quotes', 'Data Import'].map(f => (
              <Badge key={f} variant="outline" className="text-[10px] text-zinc-400 border-zinc-600">{f}</Badge>
            ))}
          </div>
        </div>

        <Separator className="border-zinc-700" />

        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="label-overline flex items-center gap-2"><Users className="h-3.5 w-3.5" /> Total Desktop Users</Label>
            <p className="text-xs text-zinc-500">Admin, dispatcher, and accounts staff who use the web portal. Includes {INCLUDED_USERS} with your plan.</p>
            <Input type="number" min={1} max={50} value={form.total_users} onChange={e => set('total_users', Math.max(1, parseInt(e.target.value) || 1))} data-testid="ea-total-users" />
            {extraUsers > 0 && <p className="text-xs text-[#ffd108]">{INCLUDED_USERS} included + {extraUsers} extra</p>}
          </div>
          <div className="space-y-2">
            <Label className="label-overline flex items-center gap-2"><Truck className="h-3.5 w-3.5" /> Total Workers with App Access</Label>
            <p className="text-xs text-zinc-500">Field operators/drivers who use the mobile app. Includes {INCLUDED_OPERATORS} with your plan.</p>
            <Input type="number" min={1} max={200} value={form.total_workers_with_app_access} onChange={e => set('total_workers_with_app_access', Math.max(1, parseInt(e.target.value) || 1))} data-testid="ea-total-workers" />
            {extraWorkers > 0 && <p className="text-xs text-[#ffd108]">{INCLUDED_OPERATORS} included + {extraWorkers} extra</p>}
          </div>
        </div>
      </div>
    );
  };

  const renderTermsStep = () => {
    const pdfUrl = `${API_URL}/api/legal/ea_terms/pdf`;
    return (
      <div className="space-y-4">
        {termsAvailable ? (
          <>
            <div className="flex items-center gap-4 p-4 bg-[#262626] rounded-lg border border-zinc-700">
              <FileText className="h-8 w-8 text-[#ffd108] shrink-0" />
              <div className="flex-1">
                <p className="text-sm text-zinc-300 font-medium">Terms &amp; Conditions</p>
                <p className="text-xs text-zinc-500 mt-0.5">Please read the full document before accepting.</p>
              </div>
              <a
                href={pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="shrink-0"
                data-testid="ea-terms-open-link"
              >
                <Button variant="outline" size="sm" type="button">
                  Open Document
                </Button>
              </a>
            </div>
            <div className="flex items-start gap-3 p-3 bg-[#262626] rounded-lg" data-testid="ea-terms-section">
              <Checkbox
                id="terms"
                checked={form.terms_accepted}
                onCheckedChange={checked => set('terms_accepted', checked)}
                data-testid="ea-terms-checkbox"
              />
              <Label htmlFor="terms" className="text-sm text-zinc-300 cursor-pointer leading-relaxed">
                I have read and accept the <strong className="text-[#ffd108]">Terms &amp; Conditions</strong>.
              </Label>
            </div>
          </>
        ) : (
          <div className="p-8 text-center space-y-3">
            <FileText className="h-12 w-12 mx-auto text-zinc-600" />
            <p className="text-zinc-400">Terms & Conditions document has not been uploaded yet.</p>
            <p className="text-xs text-zinc-500">Please contact support to proceed.</p>
          </div>
        )}
      </div>
    );
  };

  const renderReviewStep = () => {
    const industryLabel = industries.find(i => i.value === form.industry_type)?.label || form.industry_type;
    return (
      <div className="space-y-4">
        <div className="text-sm text-zinc-400 mb-2">Please review your details before submitting.</div>
        <ReviewSection title="Company" items={[
          ['Company Name', form.company_name],
          ['Address', form.address],
          ['Email', form.contact_email],
          ['Phone', form.contact_phone],
          ['ABN', form.abn || '-'],
          ['Industry', industryLabel],
        ]} />
        <ReviewSection title="First User (Admin)" items={[
          ['Name', form.admin_full_name],
          ['Email', form.admin_email],
          ['Phone', form.admin_phone || '-'],
        ]} />
        <ReviewSection title="Plan" items={[
          ['Tier', 'Advanced'],
          ['Desktop Users', `${form.total_users}`],
          ['Workers with App Access', `${form.total_workers_with_app_access}`],
        ]} />
        <div className="flex items-center gap-2 text-sm text-green-400">
          <CheckCircle className="h-4 w-4" />
          Terms & Conditions accepted
        </div>
      </div>
    );
  };

  const stepRenderers = [renderCompanyStep, renderAccountStep, renderPlanStep, renderTermsStep, renderReviewStep];

  // ── Layout ───────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-[#121212] flex flex-col items-center justify-center p-4" data-testid="ea-onboarding-page">
      {/* Header */}
      <div className="text-center mb-8">
        <img src="/assets/equipr-logo.png" alt="EQUIPR" className="h-10 mx-auto mb-4" />
        <h1 className="font-heading text-3xl font-bold text-white">Account Setup</h1>
        <p className="text-zinc-400 mt-1">Set up your EQUIPR Core account in minutes</p>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-1 mb-6" data-testid="ea-stepper">
        {STEPS.map((s, i) => (
          <div key={s.id} className="flex items-center gap-1">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                i < step ? 'bg-[#ffd108] text-black' :
                i === step ? 'bg-[#ffd108]/20 text-[#ffd108] border border-[#ffd108]' :
                'bg-zinc-800 text-zinc-500 border border-zinc-700'
              }`}
            >
              {i < step ? <CheckCircle className="h-4 w-4" /> : i + 1}
            </div>
            <span className={`text-xs hidden sm:inline ${i === step ? 'text-[#ffd108]' : 'text-zinc-500'}`}>{s.label}</span>
            {i < STEPS.length - 1 && <ChevronRight className="h-3 w-3 text-zinc-600 mx-1" />}
          </div>
        ))}
      </div>

      {/* Card */}
      <Card className="w-full max-w-xl bg-[#1c1c1c] border-zinc-800" data-testid="ea-form-card">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg text-white">{STEPS[step].label}</CardTitle>
        </CardHeader>
        <CardContent>
          {stepRenderers[step]()}

          {error && (
            <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded text-sm text-red-400" data-testid="ea-error">
              {error}
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between mt-6 pt-4 border-t border-zinc-800">
            <Button variant="ghost" onClick={handleBack} disabled={step === 0} data-testid="ea-back-btn">
              <ChevronLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            {step < STEPS.length - 1 ? (
              <Button className="btn-volt" onClick={handleNext} data-testid="ea-next-btn">
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button className="btn-volt" onClick={handleSubmit} disabled={loading} data-testid="ea-submit-btn">
                {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Creating...</> : <>Create Account <Shield className="h-4 w-4 ml-2" /></>}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Footer */}
      <p className="text-xs text-zinc-600 mt-6">
        Already have an account? <button className="text-[#ffd108] hover:underline" onClick={() => navigate('/login')}>Log in</button>
      </p>
    </div>
  );
}


function ReviewSection({ title, items }) {
  return (
    <div className="bg-[#262626] rounded-lg p-3">
      <div className="text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">{title}</div>
      {items.map(([label, value]) => (
        <div key={label} className="flex justify-between text-sm py-1">
          <span className="text-zinc-400">{label}</span>
          <span className="text-white font-medium text-right max-w-[60%] truncate">{value}</span>
        </div>
      ))}
    </div>
  );
}
